import { getCookie } from 'cookies-next';
import { fallbackLng, i18nCookieName, languages, SupportedLanguages } from 'i18n-settings';

import type { LanguageOptions, TranslateFunction } from '@/types/general';

// ------------- Type Guards -------------

/**
 * Type guard: checks that a value is a non-empty string.
 * Replaces the repetitive `!== null && !== undefined && !== ''` pattern.
 */
export function isNonEmptyString(value: string | undefined | null): value is string {
  return value !== undefined && value !== null && value !== '';
}

/**
 * Type guard: checks that a value is a valid SupportedLanguages member.
 * Combines non-empty check with enum membership validation.
 */
export function isSupportedLanguage(value: string | undefined | null): value is SupportedLanguages {
  return isNonEmptyString(value) && languages.includes(value as SupportedLanguages);
}

// ------------- Processing Functions -------------

/**
 * Extracts the base language code from a potentially regional code.
 * e.g. "it-IT" → "it", "pt-BR" → "pt", "sk" → "sk"
 */
export function getBaseLanguageCode(code: string): string {
  if (code === '') {
    return '';
  }
  return code.split('-')[0]?.toLowerCase() ?? '';
}

/**
 * Checks if two language codes refer to the same base language,
 * ignoring regional suffixes. e.g. "it" matches "it-IT", "pt" matches "pt-BR".
 */
export function isSameBaseLanguage(codeA: string, codeB: string): boolean {
  return getBaseLanguageCode(codeA) === getBaseLanguageCode(codeB);
}

/**
 * Adds a region suffix to a base language code for i18n translation key lookups.
 * e.g. "it" → "it-IT", "sk" → "sk-SK"
 */
export function processLanguageCode(code: string): string {
  if (code === undefined || code === null || code === '') {
    return '';
  }

  const parts = code.split('-');
  if (parts.length === 1) {
    return `${code}-${code.toUpperCase()}`;
  }
  return code;
}

export const languageToCountryMap: Record<string, string> = {
  'ar': 'sa',
  'bg': 'bg',
  'cs': 'cz',
  'da': 'dk',
  'de': 'de',
  'el': 'gr',
  'en': 'gb',
  'es': 'es',
  'et': 'ee',
  'fa': 'ir',
  'fil': 'ph',
  'fr': 'fr',
  'hi': 'in',
  'hr': 'hr',
  'hu': 'hu',
  'id': 'id',
  'is': 'is',
  'it': 'it',
  'ja': 'jp',
  'ka': 'ge',
  'ko': 'kr',
  'lv': 'lv',
  'mk': 'mk',
  'ms': 'my',
  'nl': 'nl',
  'pl': 'pl',
  'ps': 'af',
  'pt': 'pt',
  'pt-pt': 'pt',
  'pt-br': 'br',
  'ro': 'ro',
  'ru': 'ru',
  'sk': 'sk',
  'sl': 'si',
  'sq': 'al',
  'bs': 'ba',
  'sr': 'rs',
  'sv': 'se',
  'ta': 'in',
  'th': 'th',
  'ti': 'er',
  'tr': 'tr',
  'uk': 'ua',
  'vi': 'vn',
  'zh': 'cn',
  // Taiwan should use Taiwan flag, not China flag
  'zh-TW': 'tw',
  'zh-tw': 'tw',
};

export function getFlagUrl(code: string): string {
  const normalizedCode = code.toLowerCase();

  // First check for exact match (e.g., 'zh-TW' for Taiwan)
  if (languageToCountryMap[normalizedCode] !== undefined) {
    return `/flags/${languageToCountryMap[normalizedCode]}.svg`;
  }

  // Fall back to base language code (e.g., 'zh' -> 'cn')
  const baseCode = getBaseLanguageCode(code);
  const countryCode = languageToCountryMap[baseCode] ?? baseCode;
  return `/flags/${countryCode}.svg`;
}

export function getLanguageDisplay(code: string, translate: TranslateFunction): string {
  const processedCode = processLanguageCode(code);

  try {
    return translate(`languages.${processedCode}`);
  } catch {
    return code.toUpperCase();
  }
}

// Helper to get all supported language codes
export function getSupportedLanguageCodes(): string[] {
  return Object.values(SupportedLanguages);
}

export function getLanguageOptions(translate: TranslateFunction, availableLanguages?: string[]): LanguageOptions {
  const supportedCodes = getSupportedLanguageCodes();
  // If availableLanguages is provided, use those directly (for PDF languages)
  // Otherwise use supported codes (for UI language selection)
  const languagesToShow = availableLanguages ?? supportedCodes;

  // Only filter against supported codes if we're not given specific available languages
  const codes = availableLanguages
    ? languagesToShow
    : languagesToShow.filter(code => supportedCodes.includes(code));

  return codes.map(code => ({
    code,
    name: translate(`languages.${code}`, { fallback: code.toUpperCase() }),
  }));
}

/**
 * Gets the user's current locale with the following priority:
 * 1. Cookie language (user's explicitly chosen preference)
 * 2. Browser language (if supported)
 * 3. Fallback language
 */
export function getCurrentLocaleClient(): SupportedLanguages {
  // 1. Try to get language from cookie first (user's preference)
  const cookieLang = getCookie(i18nCookieName) as SupportedLanguages;
  if (cookieLang && languages.includes(cookieLang)) {
    return cookieLang;
  }

  // 2. Try to get browser language if we have access to navigator
  if (typeof window !== 'undefined') {
    // Get browser's preferred languages
    const browserLanguages = navigator.languages || [navigator.language];

    // Check if any of the browser languages (or their base language) match our supported languages
    for (const lang of browserLanguages) {
      // Check exact match first
      if (languages.includes(lang as SupportedLanguages)) {
        return lang as SupportedLanguages;
      }

      // Check base language match (e.g., 'en-US' matches 'en')
      const baseLang = lang.split('-')[0];
      if (languages.includes(baseLang as SupportedLanguages)) {
        return baseLang as SupportedLanguages;
      }
    }
  }

  // 3. Last resort, use fallback language
  return fallbackLng;
}

/**
 * Gets the user's current locale server-side with the following priority:
 * 1. Cookie language (user's explicitly chosen preference)
 * 2. Fallback language
 *
 * Note: Browser language detection on server-side is challenging due to Next.js
 * server component constraints. We prioritize cookie-based language selection.
 */
export async function getCurrentLocaleServer(): Promise<SupportedLanguages> {
  try {
    // Try to get language from cookie first (user's preference)
    const { cookies } = await import('next/headers');
    const cookieStore = await cookies();
    const cookieLang = cookieStore.get(i18nCookieName)?.value as SupportedLanguages;

    // Return cookie language if valid, otherwise fallback
    return (cookieLang && languages.includes(cookieLang))
      ? cookieLang
      : fallbackLng;
  } catch (error) {
    console.error('Error getting current locale server-side:', error);
    return fallbackLng;
  }
}

/**
 * Reads the editing language from the zustand persist cookie (server-side).
 * The client store writes a cookie named 'editingLanguage' in JSON format.
 */
export async function getEditingLanguageServer(): Promise<string | null> {
  try {
    const { cookies } = await import('next/headers');
    const cookieStore = await cookies();
    const raw = cookieStore.get('editingLanguage')?.value;

    if (isNonEmptyString(raw) === false) {
      return null;
    }

    const parsed = JSON.parse(decodeURIComponent(raw));
    const lang = parsed?.state?.editingLanguage;

    return typeof lang === 'string' && lang !== '' ? lang : null;
  } catch {
    return null;
  }
}

/**
 * Server-side content language resolution matching the client-side resolveContentLanguage priority.
 * Priority: editing language (from cookie) > URL param > user's UI language
 */
export async function resolveContentLanguageServer(
  urlLanguage: string | undefined | null,
  userLanguage: string,
): Promise<string> {
  const editingLanguage = await getEditingLanguageServer();

  if (isNonEmptyString(editingLanguage)) {
    return editingLanguage;
  }
  if (isNonEmptyString(urlLanguage)) {
    return urlLanguage;
  }
  return userLanguage;
}

/**
 * Validates a language string and returns it if valid, otherwise returns the fallback language.
 * Used for validating language params from URL or other sources.
 * @param language The language string to validate (can be undefined, null, or empty)
 * @returns A valid SupportedLanguages value
 */
export function getValidatedLanguageOrFallback(
  language: string | undefined | null,
): SupportedLanguages {
  if (isSupportedLanguage(language)) {
    return language;
  }
  return fallbackLng;
}

/**
 * Resolves the effective language for content editing/creation.
 *
 * This is the SINGLE SOURCE OF TRUTH for language resolution across all unit types
 * (sequence, quiz, course, autocourse). Use this instead of ad-hoc fallback chains.
 *
 * Priority:
 *   1. editingLanguage — user's selected editing language from the store (explicit choice)
 *   2. initialLanguage — language from URL/query params (edit mode)
 *   3. User's browser/UI language — from cookie or navigator
 *   4. fallbackLng ('en') — last resort
 *
 * @param initialLanguage Language from URL params (present in edit mode, undefined in create mode)
 * @param editingLanguage The user's selected editing language from useEditingLanguage()
 */
export function resolveContentLanguage(
  initialLanguage: string | undefined | null,
  editingLanguage: string | undefined | null,
): string {
  if (isNonEmptyString(editingLanguage)) {
    return editingLanguage;
  }

  if (isNonEmptyString(initialLanguage)) {
    return initialLanguage;
  }

  if (typeof window !== 'undefined') {
    return getCurrentLocaleClient();
  }

  return fallbackLng;
}

/**
 * Gets the unit language to use based on the user's language preferences and available unit languages.
 * Used when opening a unit from a list to determine the best language to display.
 * @param userLanguage The user's current language preference
 * @param availableUnitLanguages Array of languages available for the unit
 * @param fallbackLanguage Optional fallback language when unit has no available languages (defaults to userLanguage)
 * @returns The language to use for the unit
 */
export function getUnitDefaultLanguage(
  userLanguage: SupportedLanguages,
  availableUnitLanguages?: string[],
  fallbackLanguage?: string,
): string {
  const effectiveFallback = fallbackLanguage !== undefined && fallbackLanguage !== ''
    ? fallbackLanguage
    : userLanguage;

  if (availableUnitLanguages === undefined || availableUnitLanguages.length === 0) {
    return effectiveFallback;
  }

  if (availableUnitLanguages.includes(userLanguage)) {
    return userLanguage;
  }

  return availableUnitLanguages[0] || effectiveFallback;
}
